import Modal, {
  ModalBody,
  ModalHeader,
  ModalPanel,
} from 'components/common/Modal'
import { ReactNode, useState } from 'react'
import BowlIcon from 'components/common/icons/BowlIcon'
import BoxIcon from 'components/common/icons/BoxIcon'
import CalendarMinusIcon from 'components/common/icons/CalendarMinusIcon'
import {
  APIErrorDisplay,
  Button,
  ButtonLoading,
} from '@tovala/component-library'
import { useForm } from 'react-hook-form'
import { DATE_FORMATS, formatDate } from 'utils/dates'
import FormListboxRHF from 'components/common/FormListboxRHF'
import CalendarCheckIcon from 'components/common/icons/CalendarCheckIcon'
import {
  ErrorCodeMessageMapCombinedAPI,
  useBreakResumableTerms,
  useCreateUserBreak,
} from '@tovala/browser-apis-combinedapi'
import { track } from 'utils/analytics'
import { events } from 'analytics/events'

interface FormData {
  term: {
    label: string
    startDate: string
    value: string
  } | null
}

// TODO: add other error codes
const CREATE_USER_BREAK_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't pause your meal deliveries due to a technical issue on our end.",
  },
}

const LOAD_RESUMABLE_TERMS_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please reload the page to try again.',
    why: "We couldn't load the available weeks to resume deliveries.",
  },
}

const TakeABreak = ({
  nextTermID,
  onClickClose,
  userID,
}: {
  nextTermID: number | null
  onClickClose(isOnBreak: boolean): void
  userID: number
}) => {
  const [createBreakSuccess, setCreateBreakSuccess] = useState(false)

  const {
    mutate: createUserBreak,
    isPending: isCreatingUserBreak,
    isError: hasCreateUserBreakError,
    error: createUserBreakError,
  } = useCreateUserBreak()

  const {
    data: breakResumableTerms,
    isPending: isLoadingBreakResumableTerms,
    isError: hasBreakResumableTermsError,
    error: breakResumableTermsError,
  } = useBreakResumableTerms({
    userID,
  })

  const resumeTermOptions = breakResumableTerms?.resumeTermOptions ?? []
  const termOptions = breakResumableTerms
    ? resumeTermOptions.map((term) => ({
        label: `Week of ${formatDate(term.resumeTermStartDate, {
          format: DATE_FORMATS.MONTH_FULL_DAY_SUFFIX,
        })}`,
        startDate: term.resumeTermStartDate,
        value: term.resumeTermID.toString(),
      }))
    : []

  const earliestTermID = resumeTermOptions[0]?.resumeTermID ?? 0
  const latestTermID =
    resumeTermOptions[resumeTermOptions.length - 1]?.resumeTermID ?? 0

  const minWeeks =
    nextTermID !== null && earliestTermID > 0 ? earliestTermID - nextTermID : 0
  const maxWeeks =
    nextTermID !== null && latestTermID > 0 ? latestTermID - nextTermID : 0

  const { control, handleSubmit, watch } = useForm<FormData>({
    defaultValues: {
      term: null,
    },
    values:
      breakResumableTerms && resumeTermOptions.length > 0
        ? { term: termOptions[0] }
        : undefined,
  })
  const selectedTerm = watch('term')

  const onSubmit = (data: FormData) => {
    track(events.SETTINGS_TAPPED_CONFIRM_PAUSE)

    if (!data.term) {
      return
    }

    createUserBreak(
      {
        userID,
        data: {
          resumeTermID: Number(data.term.value),
        },
      },
      {
        onSuccess: () => {
          setCreateBreakSuccess(true)
        },
      },
    )
  }

  return (
    <Modal onCloseModal={() => onClickClose(createBreakSuccess)}>
      <ModalPanel>
        <div className="w-[504px] md:w-full">
          <ModalHeader onClickClose={() => onClickClose(createBreakSuccess)}>
            <span className="block text-base uppercase">Take a Break</span>
          </ModalHeader>

          <ModalBody>
            <form className="h-full" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex h-full min-h-[550px] flex-col justify-between p-6">
                {createBreakSuccess ? (
                  <>
                    <div className="grow-1 mx-auto max-w-[345px]">
                      <h2 className="mb-4 flex items-center space-x-4 text-k/28_110">
                        <div className="h-6 w-6 text-orange-2">
                          <CalendarCheckIcon />
                        </div>{' '}
                        <span>You are taking a break</span>
                      </h2>
                      <div className="space-y-6 text-body-sm">
                        <p className="font-semibold">
                          Meal deliveries will resume the week of:
                          <br />
                          {selectedTerm &&
                            formatDate(selectedTerm.startDate, {
                              format: DATE_FORMATS.MONTH_FULL_DAY_YEAR_FULL,
                            })}
                          .
                        </p>
                        <p>
                          We'll send you an email a week before your next
                          ordering deadline. If you don't choose meals by the
                          deadline, we'll fill your order for you.
                        </p>
                        <p>
                          Change your mind on pausing orders? You can resume at
                          any point.
                        </p>
                      </div>
                    </div>
                    <div className="mt-10 grid">
                      <Button
                        onClick={() => {
                          track(events.SETTINGS_TAPPED_PAUSE_DONE)

                          onClickClose(true)
                        }}
                        size="large"
                        type="button"
                      >
                        Done
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grow-1 mx-auto max-w-[345px]">
                      <h2 className="mb-4 text-k/28_110">How it works</h2>
                      <div className="space-y-6">
                        <HowItWorksItem
                          description={`Pick how many weeks you'd like to pause ${minWeeks > 0 && maxWeeks > 0 ? `— between ${minWeeks} and ${maxWeeks} weeks.` : ''}`}
                          heading="Choose how long to pause"
                          icon={<CalendarMinusIcon />}
                        />
                        <HowItWorksItem
                          description="We won't send meals during your break. Need to come back early? You can resume anytime."
                          heading="Pause deliveries"
                          icon={<BoxIcon />}
                        />
                        <HowItWorksItem
                          description="We'll remind you to choose meals a week before your break ends. Need more time? Pause again or skip orders."
                          heading="We'll remind you"
                          icon={<BowlIcon />}
                        />

                        {hasBreakResumableTermsError ? (
                          <APIErrorDisplay
                            error={breakResumableTermsError}
                            errorCodeMessageMap={LOAD_RESUMABLE_TERMS_ERRORS}
                          />
                        ) : (
                          <FormListboxRHF
                            control={control}
                            id="term"
                            isClearable={false}
                            label="When should we resume deliveries?"
                            listboxStyle="white"
                            name="term"
                            options={termOptions}
                            rules={{
                              required:
                                'Please select a week to resume deliveries',
                            }}
                          />
                        )}

                        {hasCreateUserBreakError && (
                          <APIErrorDisplay
                            error={createUserBreakError}
                            errorCodeMessageMap={CREATE_USER_BREAK_ERRORS}
                          />
                        )}
                      </div>
                    </div>

                    <div className="mt-10 grid">
                      <ButtonLoading
                        disabled={
                          isLoadingBreakResumableTerms ||
                          hasBreakResumableTermsError
                        }
                        isLoading={isCreatingUserBreak}
                        size="large"
                        type="submit"
                      >
                        Pause deliveries
                      </ButtonLoading>
                    </div>
                  </>
                )}
              </div>
            </form>
          </ModalBody>
        </div>
      </ModalPanel>
    </Modal>
  )
}

export default TakeABreak

const HowItWorksItem = ({
  heading,
  description,
  icon,
}: {
  heading: string
  description: string
  icon: ReactNode
}) => {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-4">
      <div className="h-8 w-8 text-orange-2">{icon}</div>
      <div className="space-y-1">
        <h3 className="text-k/18_120">{heading}</h3>
        <p className="text-body-sm text-grey-10">{description}</p>
      </div>
    </div>
  )
}
